import { fetchStorageBlob, isStorageProxyUrl } from "@/lib/storageFetch";
import { storageService } from "@/services/storageService";
import type { MediaItem } from "@/types";

function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [header, base64] = dataUrl.split(",");
  const mime = /data:(.*?);/.exec(header)?.[1] ?? "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mime });
}

/** Generate a JPEG thumbnail from a local or storage video URL and upload it. */
export async function uploadVideoThumbnail(
  activityId: string,
  videoUrl: string,
  sourceFileName: string,
): Promise<string> {
  const dataUrl = await generateVideoThumbnail(videoUrl);
  const baseName = sourceFileName.replace(/\.[^.]+$/, "") || "video";
  const thumbFile = dataUrlToFile(dataUrl, `${baseName}-thumb.jpg`);
  return storageService.uploadFile("activities", activityId, thumbFile);
}

/**
 * Generate a thumbnail from a video file by capturing the first frame.
 * Storage proxy URLs are fetched with auth and replayed via an object URL.
 */
export async function generateVideoThumbnail(
  videoUrl: string,
): Promise<string> {
  let objectUrl = videoUrl;
  let revokeLater = false;
  if (isStorageProxyUrl(videoUrl)) {
    const blob = await fetchStorageBlob(videoUrl);
    objectUrl = URL.createObjectURL(blob);
    revokeLater = true;
  }
  try {
    return await new Promise<string>((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.src = objectUrl;
      video.muted = true;

      video.onloadeddata = () => {
        video.currentTime = 0.1;
      };

      video.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };

      video.onerror = () => reject(new Error("Failed to load video"));
    });
  } finally {
    if (revokeLater) URL.revokeObjectURL(objectUrl);
  }
}

export function getThumbnailUrl(
  item: Pick<MediaItem, "type" | "url" | "thumbnail_url">,
): string {
  return item.type === "video" ? item.thumbnail_url || item.url : item.url;
}
